import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const TOKEN_KEY = "trueroots.jwt";
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyToken = useCallback((t) => {
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
      api.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      delete api.defaults.headers.common["Authorization"];
    }
    setToken(t || "");
  }, []);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    api.get("/auth/me").then((r) => setUser(r.data.user)).catch(() => applyToken("")).finally(() => setLoading(false));
  }, [token, applyToken]);

  const staffLogin = async (email, password) => {
    const r = await api.post("/auth/staff/login", { email, password });
    applyToken(r.data.access_token);
    setUser(r.data.user);
    return r.data.user;
  };
  const requestOtp = (phone) => api.post("/auth/farmer/request-otp", { phone }).then((r) => r.data);
  const verifyOtp = async (request_id, otp) => {
    const r = await api.post("/auth/farmer/verify-otp", { request_id, otp });
    applyToken(r.data.access_token);
    setUser(r.data.user);
    return r.data.user;
  };
  const logout = () => { applyToken(""); setUser(null); };

  return (
    <AuthCtx.Provider value={{ token, user, loading, staffLogin, requestOtp, verifyOtp, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function RequireAuth({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-16 text-center text-[var(--tr-muted)]">Loading…</div>;
  if (!user) {
    const isFarmerOnly = roles && roles.length === 1 && roles[0] === "farmer";
    window.location.href = isFarmerOnly ? "/login/farmer" : "/login/staff";
    return null;
  }
  if (roles && !roles.includes(user.role)) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="access-denied">
        <h1 className="tr-section-title text-4xl">Access denied</h1>
        <p className="mt-4 text-[var(--tr-muted)]">Your role ({user.role}) is not permitted to view this area.</p>
      </div>
    );
  }
  return children;
}
